'use client';

import type { AuthMethod } from '@ingresse/awa-analytics-web';
import { css, js } from '@ingresse/injector';
import { useEffect, useRef, useState } from 'react';

import { Environment } from '../constants/environments';
import { useEventContext } from '../hooks/contexts/event.context';
import { trackAuthEvent, type AuthEventName } from '../trackings/analyticsDataLayer';
import { User } from '../types/user';
import { logger } from '../utils/logger';
import { useUserContext } from './contexts/user.context';

/** Options accepted by the `auth-js` iframe bootstrapper (subset we pass). */
type AuthingOptions = {
  api: string;
  env: string;
  host: string;
  app: string;
  companyId: number;
  path: string;
  apikey: string;
  simpleRegistration: boolean;
};

/**
 * The `auth-js` callback map. Keys are matched against its fixed `MSGS` table, so
 * only these names are ever invoked; unknown keys are silently ignored.
 */
type AuthingCallbacks = {
  done?: (message: { content: User }) => void;
  loginSuccess?: (message: AuthOutcomeMessage) => void;
  registerSuccess?: (message: AuthOutcomeMessage) => void;
};

type AuthingInstance = {
  element?: { src: string };
};

declare global {
  interface Window {
    Authing: new (
      options: AuthingOptions,
      callbacks: AuthingCallbacks,
      targetId: string,
    ) => AuthingInstance;
  }
}

const baseUrlCdn = '//cdn.ingresse.com';
const elementId = 'auth-container';
const cssInject = { id : 'auth-css', href : `${baseUrlCdn}/auth/auth.min.css` };
const jsInject = { id : 'auth-js', src : `${baseUrlCdn}/auth/auth.min.js` };
const sdkInject = { id : 'sdk-js', src : `${baseUrlCdn}/sdk/ingresse-sdk.js` };

/** Default when the iframe resolves without ever naming a method (older auth build). */
const DEFAULT_AUTH_METHOD: AuthMethod = 'email';

/**
 * The `content` of `AUTH_LOGIN_SUCCESS` / `AUTH_REGISTER_SUCCESS` (MKT-486). Only
 * `method` is read here: these messages fire BEFORE credentials exist (registration
 * does not authenticate), so the identity is taken from `done` instead.
 */
type AuthOutcomeMessage = {
  content?: {
    method?: string | null;
  };
};

const useAuthing = (path: string) => {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [initialized, setInitialized] = useState<AuthingInstance | null>(null);
  const [loggedIn, setLoggedIn] = useState(false);
  const { event } = useEventContext();

  /**
   * MKT-486 — analytics latch for the canonical GA4 `login` / `sign_up`.
   *
   * The iframe reports the OUTCOME (`loginSuccess` / `registerSuccess`, carrying
   * `method`) and, separately, the resolved IDENTITY (`done`). Neither message alone
   * is enough: the outcome has no user, and `done` cannot tell a sign-up from a
   * login. So we latch the outcome and emit on `done`.
   */
  const authKindRef = useRef<AuthEventName | null>(null);
  const authMethodRef = useRef<AuthMethod | null>(null);
  /** Guarantees one event per mounted widget, even if `done` arrives twice. */
  const emittedRef = useRef(false);
  /**
   * The `path` the current iframe was instantiated with, or `null` before the first
   * one. auth-js leaks listeners, so the widget must NOT be rebuilt on every render
   * of this effect — but it MUST be rebuilt when `path` changes ('login' → 'register'
   * → 'trusted'), since `path` is baked into `options` and the iframe cannot be
   * re-pointed after the fact.
   */
  const authingPathRef = useRef<string | null>(null);
  /**
   * The resolved event, refreshed on every render.
   *
   * `emitAuthEvent` lives inside the widget-construction effect, which runs on
   * `[scriptLoaded, path]` and builds the widget ONCE per path — so reading `event`
   * from that closure pins it to whatever was resolved when the auth script
   * finished loading. The 500ms poll fires as soon as `window.Authing` exists,
   * which on a slow connection is before `EventProvider` has the event: `event`
   * would be `null` there, the widget is never rebuilt when it arrives, and every
   * `login`/`sign_up` of that session would go out without `organizer_tracking` —
   * the node the container needs to attribute the auth to the producer.
   */
  const eventRef = useRef(event);
  eventRef.current = event;

  const { updateUser, handleAuthenticatedUser } = useUserContext();

  const inject = () => {
    css(cssInject);
    js(jsInject);
    js(sdkInject);
  };

  /**
   * auth-js resolve o ambiente do iframe pelo `host` (não pelo `env`):
   * 'UAT' → https://<api>-auth.ingresse.com · 'HML' → auth-homolog · '' → auth (prod).
   * Sandbox roda em host UAT com api 'sbx' (→ sbx-auth.ingresse.com) e apikey PROD,
   * espelhando o canon do site_next (useAuthing).
   */
  const resolveEnvironment = (env: string) => {
    const authUrl: Record<string, string> = {
      homologation : 'HML',
    };

    if (env.startsWith('uat') || env.startsWith('bu') || env === 'sandbox' || env === 'sbx') {
      return 'UAT';
    }

    return authUrl[env] || '';
  };

  /** auth-js usa `api`/`env` como subdomínio da API do auth: 'sandbox' → 'sbx'. */
  const resolveAuthApi = (env: string) => (env === 'sandbox' ? 'sbx' : env);

  /**
   * useEffect hook to initialize the Authing object when the script is loaded and the path changes.
   * This hook runs whenever the `scriptLoaded` or `path` state changes and performs the following actions:
   * - Defines a callback function `onLoadJSCallback` that initializes the Authing object if the script is loaded.
   * - Determines if the environment is in the homologation stage.
   * - Sets the options for the Authing object based on the environment.
   * - Calls the `setInitialized` function to initialize the Authing object with the specified options and element ID.
   * - Calls the `inject` function to inject the necessary script.
   * - Executes the `onLoadJSCallback` function.
   * - Catches and logs any errors that occur during the process.
   *
   * @param scriptLoaded - Boolean state indicating if the script is loaded.
   * @param path - The current path for the Authing options.
  */
  useEffect(() => {
    try {
      const onLoadJSCallback = () => {
        if (scriptLoaded && authingPathRef.current !== path) {
          const inStage = String(Environment.NODE_ENV) !== 'production';

          // A new widget is a new auth journey: the latches below describe the
          // PREVIOUS screen's outcome and must not leak into this one (a `sign_up`
          // latched on /register would otherwise mislabel the next /login).
          authKindRef.current = null;
          authMethodRef.current = null;
          emittedRef.current = false;

          const options = {
            api : inStage ? resolveAuthApi(Environment.ENVIRONMENT) : '',
            env : inStage ? resolveAuthApi(Environment.ENVIRONMENT) : '',
            host : resolveEnvironment(Environment.ENVIRONMENT),
            app : 'embedstore',
            companyId : 1,
            path : path,
            apikey : Environment.API_KEY,
            simpleRegistration : event?.attributes.simpleRegistration?.value ?? false,
          };

          const rememberOutcome = (
            kind: AuthEventName,
            message?: AuthOutcomeMessage,
          ) => {
            // A sign_up journey reports REGISTER_SUCCESS and then LOGIN_SUCCESS (the
            // app routes to /login and the user signs in manually). `sign_up` is the
            // conversion, so it wins and is never downgraded to `login`.
            if (authKindRef.current !== 'sign_up') {
              authKindRef.current = kind;
            }

            const method = message?.content?.method;
            if (method) {
              authMethodRef.current = method;
            }
          };

          const emitAuthEvent = (user: User) => {
            if (emittedRef.current || !user?.id) return;
            emittedRef.current = true;

            void trackAuthEvent(
              authKindRef.current ?? 'login',
              { id : user.id, email : user.email, phone : user.phone },
              authMethodRef.current ?? DEFAULT_AUTH_METHOD,
              // The login happens inside this event's purchase flow, so the auth
              // event carries the producer's pixels too (Martech, 13 August 2026).
              // Read from the ref, not the closure — see `eventRef`.
              eventRef.current,
            );
          };

          const authing = new window.Authing(
            options,
            {
              // MKT-486 — outcome signals: they classify the journey and carry the
              // `method`, but hold no identity. They only feed the latch.
              loginSuccess : (message: AuthOutcomeMessage) => rememberOutcome('login', message),
              registerSuccess : (message: AuthOutcomeMessage) => rememberOutcome('sign_up', message),
              done : ({ content : user }: { content: User }) => {
                updateUser(user);
                setLoggedIn(Boolean(user));
                handleAuthenticatedUser(Boolean(user));

                if (user) emitAuthEvent(user);
              }
            }
            , elementId);

          authingPathRef.current = path;
          setInitialized(authing);
        }
      };
      inject();
      onLoadJSCallback();
    } catch (error) {
      logger.error(error as string);
    }
  }, [scriptLoaded, path]);

  /**
   * useEffect hook to check if the Authing script has been loaded.
   * This hook runs once when the component mounts and performs the following actions:
   * - Defines a function `checkIfScriptLoaded` that checks if `window.Authing` is defined.
   *   - If `window.Authing` is defined, it sets the `scriptLoaded` state to true.
   * - Sets an interval to run the `checkIfScriptLoaded` function every 500 milliseconds.
   * - Cleans up the interval when the component unmounts.
  */
  useEffect(() => {
    const checkIfScriptLoaded = () => {
      if (window.Authing) setScriptLoaded(true);
    };

    const interval = setInterval(checkIfScriptLoaded, 500);

    return () => clearInterval(interval);
  }, []);

  return { elementId, initialized, loggedIn };
};

export default useAuthing;
